import { z } from 'zod';
import { id, paginationQuery, paginatedResult } from './common.js';
import { calendarWorkspace } from './calendar.js';

export const calendarSubscriptionAccess = z.object({ canWrite: z.boolean(), notice: z.string() });
export const calendarSubscriptionQuery = paginationQuery
  .extend({
    workspace: calendarWorkspace,
    search: z.string().trim().max(100).optional(),
    status: z.enum(['all', 'active', 'paused', 'revoked', 'expired']).default('all'),
  })
  .strict();
export type CalendarSubscriptionQuery = z.infer<typeof calendarSubscriptionQuery>;
export const calendarSubscriptionCreate = z
  .object({
    workspace: calendarWorkspace,
    name: z.string().trim().min(1).max(100),
    locationId: id.max(200).optional(),
    clientRequestId: z.string().uuid(),
  })
  .strict()
  .superRefine((v, c) => {
    if ((v.workspace === 'office') !== Boolean(v.locationId))
      c.addIssue({
        code: 'custom',
        message:
          'Office subscriptions need one selected location; professional calendars use your own assignments.',
      });
  });
export type CalendarSubscriptionCreate = z.infer<typeof calendarSubscriptionCreate>;
export const calendarSubscriptionMutation = z
  .object({
    action: z.enum(['rename', 'pause', 'resume', 'rotate', 'revoke']),
    name: z.string().trim().min(1).max(100).optional(),
    expectedRevision: z.number().int().positive(),
    clientRequestId: z.string().uuid(),
  })
  .strict()
  .refine((v) => v.action !== 'rename' || !!v.name, { message: 'Enter a calendar name.' });
export type CalendarSubscriptionMutation = z.infer<typeof calendarSubscriptionMutation>;
export const calendarSubscription = z.object({
  id,
  name: z.string(),
  workspace: calendarWorkspace,
  locationId: id.nullable(),
  locationName: z.string().nullable(),
  status: z.enum(['active', 'paused', 'revoked', 'expired']),
  revision: z.number().int().positive(),
  canWrite: z.boolean(),
  expiresAt: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CalendarSubscription = z.infer<typeof calendarSubscription>;
export const calendarSubscriptionPage = paginatedResult(calendarSubscription);
export const calendarSubscriptionIssued = z.object({
  subscription: calendarSubscription,
  feedUrl: z.string().url().nullable(),
  replayed: z.boolean(),
});
export const calendarSubscriptionLocationsQuery = paginationQuery
  .extend({ search: z.string().trim().max(100).optional() })
  .strict();
export const calendarSubscriptionLocationPage = paginatedResult(z.object({ id, name: z.string() }));
